import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/Dialog';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { LessonTitle } from '@/routes/_embaUserLayout/-components/LessonTitle';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import classNames from 'classnames';
import type React from 'react';
import AttentionIcon from './-assets/icon_attention.svg';
import firstCleardImg from './-assets/images/firstCleared.png';
import styles from './index.module.scss';

import { AnswerListItem } from './-components/AnswerListItem';

export const USER_SKILL_CHECK_TEST_RESULT = graphql(/* GraphQL */ `
  fragment EmbaUserSkillCheckTestResult on UserSkillCheckTestResult {
    id
    questionsCount
    correctCount
    correctRatePercentage
    isCleared
    isAllCorrect
    embaCourseSkillCheckTest{
      embaCourse{
        id
        name
      }
    }
    skillCheckTestAnswers{
      id
      ...EmbaUserSkillCheckTestAnswer
    }
    correctAnswers{
      id
      ...EmbaUserSkillCheckTestAnswer
    }
    embaUserAttendance{
      id
      status
      certificate{
        fileRedirectUrl
      }
    }
  }
`);

export type SkillCheckTestResultPageProps = {
  embaUserSkillCheckTestResult: FragmentType<
    typeof USER_SKILL_CHECK_TEST_RESULT
  >;
  navigateToSkillCheckTest: () => void;
  isTimeUpModalOpen: boolean;
  closeTimeUpModal: () => void;
  isFirstClearedModalOpen: boolean;
  closeFirstClearedModal: () => void;
};

export const SkillCheckTestResultPage = ({
  embaUserSkillCheckTestResult,
  navigateToSkillCheckTest,
  isTimeUpModalOpen,
  closeTimeUpModal,
  isFirstClearedModalOpen,
  closeFirstClearedModal,
}: SkillCheckTestResultPageProps) => {
  const embaUserSkillCheckTestResultData = useFragment(
    USER_SKILL_CHECK_TEST_RESULT,
    embaUserSkillCheckTestResult,
  );

  const answers = embaUserSkillCheckTestResultData.isAllCorrect
    ? embaUserSkillCheckTestResultData.correctAnswers
    : embaUserSkillCheckTestResultData.skillCheckTestAnswers;
  return (
    <>
      <LessonTitle
        lessonName="スキルチェックテスト"
        chapterName={
          embaUserSkillCheckTestResultData.embaCourseSkillCheckTest.embaCourse
            .name
        }
      />
      <div className={styles.skillCheckTestResult}>
        {!embaUserSkillCheckTestResultData.isAllCorrect && (
          <p className={styles.annotation}>
            ※一度正解した問題は全問正解するまで表示されなくなります
          </p>
        )}
        <div className={styles.resultArea}>
          <div className={styles.resultDetail}>
            <p
              className={classNames(styles.resultDetailLabel, {
                [styles.success]: embaUserSkillCheckTestResultData.isCleared,
                [styles.failed]: !embaUserSkillCheckTestResultData.isCleared,
              })}
            >
              {embaUserSkillCheckTestResultData.isCleared ? '合格' : '不合格'}
            </p>
            <p
              className={classNames(styles.resultDetailScore, {
                [styles.success]: embaUserSkillCheckTestResultData.isCleared,
                [styles.failed]: !embaUserSkillCheckTestResultData.isCleared,
              })}
            >
              {embaUserSkillCheckTestResultData.correctRatePercentage}{' '}
              <span>%</span>
            </p>
            <p className={styles.resultDetailCount}>
              ({embaUserSkillCheckTestResultData.correctCount}/
              {embaUserSkillCheckTestResultData.questionsCount})
            </p>
          </div>
          <div className={styles.resultActionsArea}>
            {!embaUserSkillCheckTestResultData.isAllCorrect && (
              <Button size="medium" onClick={navigateToSkillCheckTest}>
                再受検する
              </Button>
            )}
            {embaUserSkillCheckTestResultData.isCleared &&
              embaUserSkillCheckTestResultData.embaUserAttendance.certificate
                ?.fileRedirectUrl && (
                <a
                  className={classNames(
                    styles.showCertificateButton,
                    'primaryShadow',
                  )}
                  href={
                    embaUserSkillCheckTestResultData.embaUserAttendance
                      .certificate.fileRedirectUrl
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  修了証を見る
                </a>
              )}
          </div>
        </div>
        {answers.length > 0 && (
          <div className={styles.questionsArea}>
            <ul className={styles.questions}>
              {answers.map((answer) => (
                <AnswerListItem key={answer.id} answer={answer} />
              ))}
            </ul>
          </div>
        )}
        {isTimeUpModalOpen && (
          <div className={styles.timeUpModal}>
            <div className={styles.timeUpModalInner}>
              <p className={styles.timeUpModalText}>
                <img src={AttentionIcon} alt="" />
                <span>時間切れでテストが終了しました</span>
              </p>
              <button
                type="button"
                onClick={closeTimeUpModal}
                className={classNames(
                  styles.timeUpModalCloseButton,
                  'primaryShadow',
                )}
              >
                閉じる
              </button>
            </div>
          </div>
        )}
        <Dialog
          open={isFirstClearedModalOpen}
          onOpenChange={closeFirstClearedModal}
        >
          <VisuallyHidden>
            <DialogDescription>
              スキルチェック初回合格時のモーダル
            </DialogDescription>
          </VisuallyHidden>

          <DialogContent>
            <div className={styles.firstClearedDialog}>
              <div className={styles.firstClearedDialog_closeWrap}>
                <p
                  className={styles.firstClearedDialog_closeWrap_closeText}
                  onClick={() => {
                    closeFirstClearedModal();
                  }}
                >
                  ×
                </p>
              </div>
              <DialogTitle className={styles.firstClearedDialog_title}>
                合格です！お疲れ様でした！
              </DialogTitle>
              <div className={styles.firstClearedDialog_imgWrap}>
                <img
                  className={styles.firstClearedImage}
                  src={firstCleardImg}
                  alt="合格トロフィー"
                />
              </div>
              <div className={styles.firstClearedDialog_message}>
                <p>
                  スキルチェックテストの正解率が70%を超えたので、
                  <br />「
                  {
                    embaUserSkillCheckTestResultData.embaCourseSkillCheckTest
                      .embaCourse.name
                  }
                  」は修了となります。
                </p>
              </div>
              {embaUserSkillCheckTestResultData.isCleared &&
                embaUserSkillCheckTestResultData.embaUserAttendance.certificate
                  ?.fileRedirectUrl && (
                  <a
                    className={styles.showCertificateButtonOnModal}
                    href={
                      embaUserSkillCheckTestResultData.embaUserAttendance
                        .certificate.fileRedirectUrl
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    修了証を見る
                  </a>
                )}
              {!embaUserSkillCheckTestResultData.isAllCorrect && (
                <Button
                  size="medium"
                  onClick={navigateToSkillCheckTest}
                  className={styles.retakeExamButton}
                >
                  残りの問題を再受検する
                </Button>
              )}
              <p
                className={styles.showAnsweredQuestionsText}
                onClick={() => {
                  closeFirstClearedModal();
                }}
              >
                回答した問題を確認する
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
